import React, { useEffect, useRef } from 'react';
import moment, { Moment } from 'moment';
import { formatPhoneNumber, getPhoneInputMask } from 'utils';
import type { JsonObject } from '@/app-package';
import { ListingDoc } from '@bb/domain/dist/schemas';
import { DatePicker, TimePicker } from '../../react-common/Pickers';
import { ErrorType } from './types';

export interface RegisterOfferPanelProps {
  canRegisterOffer: () => boolean;
  user: any;
  offerDate: string;
  irrevocable?: string;
  phone?: string;
  recaptchaOpen?: boolean;
  openFileUpload: () => void;
  shouldSubmitOfferDoc: boolean;
  attachmentForm801: { filename: string; url: string } | null;
  handleChange: (changes: any, errorName?: string) => void;
  error: ErrorType;
  listing: JsonObject<ListingDoc>;
}

const RegisterOfferPanel: React.FC<RegisterOfferPanelProps> = ({
  canRegisterOffer,
  user,
  offerDate,
  irrevocable,
  phone,
  recaptchaOpen,
  openFileUpload,
  shouldSubmitOfferDoc,
  attachmentForm801 = {},
  handleChange,
  error,
  listing,
}) => {
  const { org } = user;
  const [dateValue, setDateValue] = React.useState(null);
  const [timeValue, setTimeValue] = React.useState(null);

  const phoneRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (phoneRef.current) {
      getPhoneInputMask().mask(phoneRef.current);
    }
  }, [phoneRef.current]);

  const handleDateChange = (date: Moment, value: string) => {
    const day = moment(date).dayOfYear();
    const hour = moment(irrevocable || date).hours();
    const minute = moment(irrevocable || date).minutes();
    const exportDate = moment(date)
      .dayOfYear(day)
      .hours(hour)
      .minutes(minute)
      .seconds(0)
      .milliseconds(0)
      .toDate();
    setDateValue(value);
    handleChange({ irrevocable: exportDate }, 'irrevocable');
  };

  const handleTimeChange = (time: Moment, value: string) => {
    const hour = moment(time).hours();
    const min = moment(time).minutes();
    const exportDate = moment(irrevocable)
      .hours(hour)
      .minutes(min)
      .seconds(0)
      .milliseconds(0)
      .toDate();
    setTimeValue(value);
    handleChange({ irrevocable: exportDate }, 'irrevocable');
  };

  const renderInitials = () => {
    const { name } = user;
    const wordArray = name.split(' ').filter((word: string) => word);
    const firstWord = wordArray[0].toUpperCase();
    const lastWord = wordArray[wordArray.length - 1].toUpperCase();
    return `${firstWord[0]}${lastWord[0] || ''}`;
  };

  const getEmail = () => {
    const filteredEmail = user.emails?.find(
      (email: string) => user.email !== email,
    );

    return filteredEmail || user.email;
  };

  return (
    <div className="col-md-8 right-panel">
      <div className="hpanel">
        <div className="panel-body">
          <h3>Register Offer</h3>
          {canRegisterOffer() ? (
            <form>
              <div className="col-lg-6">
                <div className="form-group agent-info">
                  <div className="agent-info-initials">{renderInitials()}</div>
                  <div>
                    <div className="agent-info-name">{user.name}</div>
                    <div>{org.name}</div>
                  </div>
                </div>
                <div className="form-group contact-info">
                  <h4>Contact Info</h4>
                  <p>{org.address}</p>
                  <p>{formatPhoneNumber(org.phone)}</p>
                  <p>{getEmail()}</p>
                </div>
                <div className="form-group">
                  <h4>Mobile Number</h4>
                  <input
                    type="text"
                    className="form-control phone-input"
                    value={phone}
                    onChange={e =>
                      handleChange({ phone: e.target.value }, 'phone')
                    }
                    disabled={recaptchaOpen}
                    required
                    name="phone"
                    placeholder="Mobile"
                    data-cy="offer-coop-phone"
                    ref={phoneRef}
                  />
                  {error.phone && (
                    <label className="error">Phone is required.</label>
                  )}
                </div>
              </div>
              <div className="col-lg-6">
                {shouldSubmitOfferDoc && (
                  <div className="form-group">
                    <a
                      className="btn btn-primary form-801"
                      onClick={openFileUpload}
                    >
                      ATTACH OFFER DOC
                    </a>
                    {attachmentForm801 ? (
                      <div>
                        <h5>Attachment</h5>
                        <div className="attachment">
                          <a
                            href={attachmentForm801.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {attachmentForm801.filename}
                          </a>
                          <button
                            disabled={recaptchaOpen}
                            onClick={() =>
                              handleChange({ attachmentForm801: null })
                            }
                          >
                            <i className="pe-7s-trash fat"></i>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <h5>No form attached.</h5>
                    )}
                    {error.form801 && (
                      <label className="error">Offer Doc is required.</label>
                    )}
                  </div>
                )}
                <div className="form-group">
                  <h4>Irrevocability</h4>
                  <div>
                    <label>Date:</label>
                  </div>
                  <DatePicker
                    inputVariant="outlined"
                    inputValue={dateValue}
                    value={irrevocable}
                    onChange={handleDateChange}
                    format="YYYY-MM-DD"
                    variant="inline"
                    autoOk
                    disablePast
                    data-cy="offer-set-irrevocable"
                    disabled={recaptchaOpen}
                    {...(listing?.status === 'Coming Soon' && {
                      minDate: new Date(listing.coming_soon_date!),
                    })}
                  />
                  {irrevocable && (
                    <div className="form-group">
                      <div>
                        <label>Time:</label>
                      </div>
                      <TimePicker
                        inputVariant="outlined"
                        inputValue={timeValue}
                        value={irrevocable}
                        onChange={handleTimeChange}
                        mask="__:__ _M"
                        variant="inline"
                        autoOk
                        disabled={recaptchaOpen}
                      />
                    </div>
                  )}
                  {error.irrevocable && (
                    <div>
                      <label className="error">
                        Irrevocable date is required.
                      </label>
                    </div>
                  )}
                  {error.pastDate && (
                    <label className="error">
                      Irrevocable date must be in the future.
                    </label>
                  )}
                  {error.invalidDate && (
                    <label className="error">Invalid irrevocable date.</label>
                  )}
                  {error.invalidTime && (
                    <label className="error">Invalid irrevocable time.</label>
                  )}
                  {error.beforeCsDate && (
                    <label className="error">
                      Irrevocable date must be after coming soon date.
                    </label>
                  )}
                </div>
              </div>
            </form>
          ) : (
            <>
              <div>
                <p>
                  Online offer registration will be available on the offer date:
                </p>
              </div>
              <div>
                <p>{moment(offerDate).format('MMM DD, YYYY h:mm A')}</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterOfferPanel;
